/*
  Wild Paws and Claws – homepage helpers (custom elements, re-init safe in the theme editor).
  <wpc-scroller>  prev/next arrows for a scroll-snap track ([data-wpc-track]); uses the visible track.
  <wpc-tabs>      accessible tabs (role=tablist, arrow/Home/End keys) that switch [role=tabpanel].
*/
(() => {
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (!customElements.get('wpc-scroller')) {
    class WpcScroller extends HTMLElement {
      connectedCallback() {
        this.prev = this.querySelector('[data-wpc-prev]');
        this.next = this.querySelector('[data-wpc-next]');
        this.onClickPrev = () => this.go(-1);
        this.onClickNext = () => this.go(1);
        this.onUpdate = () => this.update();
        this.prev?.addEventListener('click', this.onClickPrev);
        this.next?.addEventListener('click', this.onClickNext);
        this.addEventListener('wpc:tabchange', this.onUpdate);
        this.tracks = [...this.querySelectorAll('[data-wpc-track]')];
        this.tracks.forEach((t) => t.addEventListener('scroll', this.onUpdate, { passive: true }));
        this.ro = new ResizeObserver(this.onUpdate);
        this.tracks.forEach((t) => this.ro.observe(t));
        this.update();
      }
      disconnectedCallback() {
        this.prev?.removeEventListener('click', this.onClickPrev);
        this.next?.removeEventListener('click', this.onClickNext);
        this.removeEventListener('wpc:tabchange', this.onUpdate);
        this.tracks?.forEach((t) => t.removeEventListener('scroll', this.onUpdate));
        this.ro?.disconnect();
      }
      get track() {
        return this.tracks.find((t) => t.offsetParent !== null) || this.tracks[0];
      }
      go(dir) {
        const t = this.track;
        if (!t) return;
        t.scrollBy({ left: dir * t.clientWidth * 0.9, behavior: reduceMotion() ? 'auto' : 'smooth' });
      }
      update() {
        const t = this.track;
        if (!t) return;
        const max = t.scrollWidth - t.clientWidth - 2;
        const atStart = t.scrollLeft <= 2;
        const atEnd = t.scrollLeft >= max;
        if (this.prev) this.prev.disabled = atStart;
        if (this.next) this.next.disabled = atEnd;
        this.toggleAttribute('data-static', max <= 0);
      }
    }
    customElements.define('wpc-scroller', WpcScroller);
  }

  if (!customElements.get('wpc-tabs')) {
    class WpcTabs extends HTMLElement {
      connectedCallback() {
        this.tabs = [...this.querySelectorAll('[role="tab"]')];
        this.onClick = (e) => {
          const tab = e.target.closest('[role="tab"]');
          if (tab) this.select(tab);
        };
        this.onKey = (e) => {
          const i = this.tabs.indexOf(document.activeElement);
          if (i < 0) return;
          let n = null;
          if (e.key === 'ArrowRight') n = (i + 1) % this.tabs.length;
          else if (e.key === 'ArrowLeft') n = (i - 1 + this.tabs.length) % this.tabs.length;
          else if (e.key === 'Home') n = 0;
          else if (e.key === 'End') n = this.tabs.length - 1;
          if (n === null) return;
          e.preventDefault();
          this.select(this.tabs[n]);
          this.tabs[n].focus();
        };
        this.addEventListener('click', this.onClick);
        this.addEventListener('keydown', this.onKey);
      }
      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('keydown', this.onKey);
      }
      select(tab) {
        const root = this.closest('[data-wpc-tabs-root]') || document;
        this.tabs.forEach((t) => {
          const on = t === tab;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
          const panel = root.querySelector('#' + t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !on;
        });
        this.dispatchEvent(new CustomEvent('wpc:tabchange', { bubbles: true }));
      }
    }
    customElements.define('wpc-tabs', WpcTabs);
  }
})();
